import type { ReactElement } from 'react';
import { FolderOpen, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { TextHighlight } from '@/components/shared/TextHighlight';
import { useDataTableSearch } from '@/components/DataTable/DataTableSearchContext';
import type { SkillDataTableItem } from '@kirin/api-client/contracts/skills';
import { sourceLabel } from './constants';

interface CellProps {
  row: SkillDataTableItem;
}

/**
 * Name over file path, per the ticket's column spec.
 *
 * Renders `displayName` — the directory as its author cased it. The folded
 * `name` is the match key and is what any rule written from this row sends;
 * showing it would render `PDF-Processing` as `pdf-processing`.
 */
export function NameCell({ row }: CellProps): ReactElement {
  const searchTerm = useDataTableSearch();
  return (
    <div className="flex min-w-0 flex-col gap-0.5 py-1">
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm font-medium" title={row.displayName}>
          <TextHighlight text={row.displayName} searchTerm={searchTerm} />
        </span>
        {/* The same name in another source is a different skill, marked
            separately — the badge is what tells two such rows apart without
            adding a column the ticket does not ask for. */}
        <Badge
          variant="secondary"
          className="shrink-0 whitespace-nowrap font-normal"
        >
          {sourceLabel(row.source)}
        </Badge>
      </span>
      <MiddleTruncatedPath
        path={row.filePath}
        copyText={
          row.filePaths.length > 1 ? row.filePaths.join('\n') : undefined
        }
        searchTerm={searchTerm}
        className="text-xs text-muted-foreground"
      />
    </div>
  );
}

interface CountCellProps extends CellProps {
  onClick?: () => void;
}

function CountCell({
  count,
  singular,
  plural,
  skillName,
  icon,
  onClick,
}: {
  count: number;
  /** Singular noun, used when `count` is 1. */
  singular: string;
  /** Plural noun. */
  plural: string;
  /** Skill display name, so the button names which row it opens. */
  skillName: string;
  icon: ReactElement;
  onClick?: () => void;
}): ReactElement {
  if (count === 0) {
    return <span className="text-sm text-muted-foreground">0</span>;
  }
  if (!onClick) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        {icon}
        {count}
      </span>
    );
  }
  return (
    <Button
      variant="link"
      className="h-auto justify-start p-0 text-sm text-primary hover:underline"
      aria-label={`${count} ${count === 1 ? singular : plural} for ${skillName}`}
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
    >
      <span className="inline-flex items-center gap-1.5">
        {icon}
        {count}
      </span>
    </Button>
  );
}

/** How many projects use the skill. */
export function ProjectsCell({ row, onClick }: CountCellProps): ReactElement {
  return (
    <CountCell
      count={row.projects.length}
      singular="project"
      plural="projects"
      skillName={row.displayName}
      icon={<FolderOpen aria-hidden className="size-3.5 shrink-0" />}
      onClick={onClick}
    />
  );
}

/** How many users have the skill. */
export function UsersCell({ row, onClick }: CountCellProps): ReactElement {
  return (
    <CountCell
      count={row.users.length}
      singular="user"
      plural="users"
      skillName={row.displayName}
      icon={<Users aria-hidden className="size-3.5 shrink-0" />}
      onClick={onClick}
    />
  );
}
